import { apiClient } from "../../../client";
import { Filter, StandardAxiosConfig, getBaseDataURL } from "../../../common";
import { CreateOperatorInput, TeamMember, UpdateOperatorLoginInput } from "@domain/CRM/teamMember";
import { DataAPI_Base } from "../base";

/** Active team members without a login password — the set-password candidates. */
export const ACTIVE_PASSWORDLESS_MEMBERS_FILTER: Filter<TeamMember> = {
	type: "AND",
	fields: [{ status: "Active" }, { password: { operator: "is_empty" } }],
};

export class TeamMembersAPI extends DataAPI_Base<TeamMember> {
	constructor() {
		super("team-members", TeamMember);
	}

	async updateOperatorLogin(input: UpdateOperatorLoginInput): Promise<TeamMember> {
		const url = `${getBaseDataURL("team-members")}/set-password`;
		// Backend SetPasswordRequest uses camelCase aliases (ModifiedBaseModel),
		// so the UpdateOperatorLoginInput can be posted as-is.
		const response = await apiClient.post(url, input, StandardAxiosConfig());
		return response.data as TeamMember;
	}

	async createOperator(input: CreateOperatorInput): Promise<TeamMember> {
		const url = `${getBaseDataURL("team-members")}/create`;
		// Backend CreateTeamMemberRequest uses camelCase aliases (ModifiedBaseModel),
		// so the CreateOperatorInput can be posted as-is.
		const response = await apiClient.post(url, input, StandardAxiosConfig());
		return response.data as TeamMember;
	}

	async createOperatorWithDefaultPassword(
		input: Omit<CreateOperatorInput, "password">,
	): Promise<{ teamMember: TeamMember; password: string }> {
		const url = `${getBaseDataURL("team-members")}/create`;
		// No password is sent: the server issues its default operator password and returns it
		// once as `defaultPassword`, so the value never ships in this bundle.
		const response = await apiClient.post(url, { ...input, useDefaultPassword: true }, StandardAxiosConfig());
		return withDefaultPassword(response.data);
	}

	async resetOperatorLoginToDefault(teamMemberId: string): Promise<{ teamMember: TeamMember; password: string }> {
		const url = `${getBaseDataURL("team-members")}/set-password`;
		const response = await apiClient.post(url, { teamMemberId, useDefaultPassword: true }, StandardAxiosConfig());
		return withDefaultPassword(response.data);
	}
}

/** Split a login issued on the server-held default into the record and the password the
 *  server returned (once) for display. */
function withDefaultPassword(data: TeamMember & { defaultPassword?: string | null }): {
	teamMember: TeamMember;
	password: string;
} {
	const { defaultPassword, ...teamMember } = data;
	if (!defaultPassword) throw new Error("The server did not return the default operator password.");
	return { teamMember: teamMember as TeamMember, password: defaultPassword };
}
